import { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getStats, getParametres, getComptes, createCompte, updateCompte, deleteCompte } from '../services/api'
import { Amount, formatMoneySimple } from '../utils/formatMoney'
import Gauge from '../components/Gauge'
import Icon from '../components/Icon'
import { useToast } from '../components/ToastProvider'
import { useConfirm } from '../components/ConfirmProvider'
import { useFreshData } from '../hooks/useFreshData'
import { isAbortError } from '../utils/abort'

/** Valide un formulaire de compte. Retourne un objet d'erreurs par champ (vide si tout est bon). */
function validateCompteForm({ nom, montant, montant_decouvert }) {
  const errors = {}
  const nomClean = (nom || '').trim()
  if (!nomClean) {
    errors.nom = 'Donnez un nom à ce compte'
  } else if (nomClean.length > 100) {
    errors.nom = 'Nom trop long (100 caractères max.)'
  }
  const montantValue = parseFloat(montant)
  if (montant === '' || montant == null || Number.isNaN(montantValue)) {
    errors.montant = 'Saisissez un solde'
  } else if (Math.abs(montantValue) > 999999999.99) {
    errors.montant = 'Montant trop élevé'
  }
  if (montant_decouvert !== '' && montant_decouvert != null) {
    const decouvertValue = parseFloat(montant_decouvert)
    if (Number.isNaN(decouvertValue)) {
      errors.montant_decouvert = 'Saisissez un nombre'
    } else if (decouvertValue < 0) {
      errors.montant_decouvert = 'Le découvert ne peut pas être négatif'
    }
  }
  return errors
}

/** Tuile de statistique : surtitre, montant, et un repère visuel discret. */
function StatTile({ label, value, devise, tone = 'none', icon }) {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="overline">{label}</h3>
        {icon && <Icon name={icon} size={16} className="text-ink-disabled" />}
      </div>
      <Amount
        value={value}
        devise={devise}
        tone={tone}
        className="mt-2 block text-2xl font-semibold"
      />
    </div>
  )
}

const PERIODES = [
  { key: 'mois', label: 'Ce mois' },
  { key: 'annee', label: 'Cette année' },
  { key: 'all', label: 'Tout' },
]

export default function Dashboard({ active = true }) {
  const [stats, setStats] = useState({})
  const [parametres, setParametres] = useState({ devise: 'EUR' })
  const [comptes, setComptes] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false) // Rafraîchissement en arrière-plan (ne cache pas le contenu)
  const [filter, setFilter] = useState({ mois: null, annee: null })
  const [showAddCompteModal, setShowAddCompteModal] = useState(false)
  const [addCompteForm, setAddCompteForm] = useState({ nom: '', montant: '', montant_decouvert: '0' })
  const [addingCompte, setAddingCompte] = useState(false)
  const [selectedCompte, setSelectedCompte] = useState(null)
  const [editingCompte, setEditingCompte] = useState(false)
  const [editCompteForm, setEditCompteForm] = useState({ nom: '', montant: '', montant_decouvert: '0' })
  const [savingCompte, setSavingCompte] = useState(false)
  const [deletingCompte, setDeletingCompte] = useState(false)
  const [compteErrors, setCompteErrors] = useState({})
  const location = useLocation()
  const navigate = useNavigate()
  const hasLoadedRef = useRef(false)
  const toast = useToast()
  const confirm = useConfirm()

  const loadData = useCallback(async ({ forceRefresh = false, signal } = {}) => {
    const useBackground = hasLoadedRef.current
    try {
      if (useBackground) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      const [statsData, paramsData, comptesData] = await Promise.all([
        getStats(filter, forceRefresh, { signal }).catch(err => {
          if (isAbortError(err)) throw err
          console.error('Erreur lors du chargement des stats:', err)
          return null
        }),
        getParametres(forceRefresh, { signal }).catch(err => {
          if (isAbortError(err)) throw err
          console.error('Erreur lors du chargement des paramètres:', err)
          return { devise: 'EUR', decouvert_autorise: 0 }
        }),
        getComptes(forceRefresh, { signal }).catch(err => {
          if (isAbortError(err)) throw err
          return []
        }),
      ])
      if (signal?.aborted) return

      if (statsData) {
        setStats(statsData)
      }
      if (paramsData) {
        setParametres(paramsData)
      }
      if (comptesData && Array.isArray(comptesData)) {
        setComptes(comptesData)
      }
      hasLoadedRef.current = true
    } catch (error) {
      if (isAbortError(error) || signal?.aborted) return
      console.error('Erreur lors du chargement des données:', error)
    } finally {
      if (signal?.aborted) return
      setRefreshing(false)
      setLoading(false)
    }
  }, [filter])

  useFreshData({ onRefresh: loadData, enabled: active })

  // Rediriger vers premier-compte si l'utilisateur n'a aucun compte (après chargement)
  useEffect(() => {
    if (!loading && location.pathname === '/dashboard' && Array.isArray(comptes) && comptes.length === 0 && hasLoadedRef.current) {
      navigate('/premier-compte', { replace: true })
    }
  }, [loading, comptes, location.pathname, navigate])

  const closeCompteModals = () => {
    setSelectedCompte(null)
    setEditingCompte(false)
    setCompteErrors({})
  }

  const handleEditCompte = () => {
    setEditCompteForm({
      nom: selectedCompte.nom || '',
      montant: String(selectedCompte.montant ?? ''),
      montant_decouvert: String(selectedCompte.montant_decouvert ?? '0')
    })
    setCompteErrors({})
    setEditingCompte(true)
  }

  const handleUpdateCompteSubmit = async (e) => {
    e.preventDefault()
    if (savingCompte) return
    const errors = validateCompteForm(editCompteForm)
    setCompteErrors(errors)
    if (Object.keys(errors).length > 0) return

    const montant = parseFloat(editCompteForm.montant)
    const soldeChange = Math.abs(montant - (parseFloat(selectedCompte.montant) || 0)) >= 0.005

    // Modifier le solde crée une opération d'actualisation côté serveur :
    // un effet de bord invisible mérite d'être annoncé avant d'être appliqué.
    if (soldeChange) {
      const confirmed = await confirm({
        title: 'Actualiser le solde de ce compte ?',
        description: 'Pour garder l’historique cohérent, l’écart sera enregistré comme une opération d’actualisation.',
        confirmLabel: 'Actualiser',
        details: [
          { label: 'Solde actuel', value: formatMoneySimple(parseFloat(selectedCompte.montant) || 0, parametres?.devise || 'EUR') },
          { label: 'Nouveau solde', value: formatMoneySimple(montant, parametres?.devise || 'EUR') },
          {
            label: 'Opération créée',
            value: `${montant - (parseFloat(selectedCompte.montant) || 0) > 0 ? '+' : '-'} ${formatMoneySimple(Math.abs(montant - (parseFloat(selectedCompte.montant) || 0)), parametres?.devise || 'EUR')}`,
          },
        ],
      })
      if (!confirmed) return
    }

    setSavingCompte(true)
    try {
      await updateCompte(selectedCompte.id, {
        nom: editCompteForm.nom.trim(),
        montant,
        montant_decouvert: parseFloat(editCompteForm.montant_decouvert) || 0,
      })
      closeCompteModals()
      toast.success(`Compte « ${editCompteForm.nom.trim()} » mis à jour`)
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Erreur lors de la mise à jour')
    } finally {
      setSavingCompte(false)
    }
  }

  const closeAddCompteModal = () => {
    setShowAddCompteModal(false)
    setAddCompteForm({ nom: '', montant: '', montant_decouvert: '0' })
    setCompteErrors({})
  }

  const handleAddCompteSubmit = async (e) => {
    e.preventDefault()
    if (addingCompte) return
    const errors = validateCompteForm(addCompteForm)
    setCompteErrors(errors)
    if (Object.keys(errors).length > 0) return

    const nom = addCompteForm.nom.trim()
    setAddingCompte(true)
    try {
      await createCompte({
        nom,
        montant: parseFloat(addCompteForm.montant),
        montant_decouvert: parseFloat(addCompteForm.montant_decouvert) || 0,
      })
      closeAddCompteModal()
      toast.success(`Compte « ${nom} » créé`)
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Erreur lors de la création du compte')
    } finally {
      setAddingCompte(false)
    }
  }

  const handleDeleteCompte = async () => {
    if (!selectedCompte || deletingCompte) return
    const devise = parametres?.devise || 'EUR'
    const soldeCompte = typeof stats?.soldes_comptes_a_ce_jour?.[selectedCompte.id] === 'number'
      ? stats.soldes_comptes_a_ce_jour[selectedCompte.id]
      : (Number(selectedCompte.montant) || 0)

    const confirmed = await confirm({
      title: 'Supprimer ce compte ?',
      description: 'La suppression n’est possible que si aucune opération n’y est rattachée.',
      danger: true,
      confirmLabel: 'Supprimer',
      details: [
        { label: 'Compte', value: selectedCompte.nom },
        { label: 'Solde à ce jour', value: formatMoneySimple(soldeCompte, devise) },
        { label: 'Découvert autorisé', value: formatMoneySimple(Number(selectedCompte.montant_decouvert) || 0, devise) },
      ],
      warning: comptes.length <= 1
        ? 'C’est votre dernier compte : vous devrez en créer un nouveau pour continuer à saisir des opérations.'
        : undefined,
    })
    if (!confirmed) return

    const nom = selectedCompte.nom
    setDeletingCompte(true)
    try {
      await deleteCompte(selectedCompte.id)
      closeCompteModals()
      toast.success(`Compte « ${nom} » supprimé`)
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Erreur lors de la suppression du compte')
    } finally {
      setDeletingCompte(false)
    }
  }

  // Échap ferme la fenêtre ouverte (détail de compte ou création).
  useEffect(() => {
    if (!selectedCompte && !showAddCompteModal) return undefined
    const onKeyDown = (e) => {
      if (e.key !== 'Escape' || savingCompte || addingCompte || deletingCompte) return
      if (showAddCompteModal) closeAddCompteModal()
      else closeCompteModals()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompte, showAddCompteModal, savingCompte, addingCompte, deletingCompte])

  const setQuickFilter = (type) => {
    const now = new Date()
    if (type === 'mois') {
      setFilter({ mois: String(now.getMonth() + 1).padStart(2, '0'), annee: String(now.getFullYear()) })
    } else if (type === 'annee') {
      setFilter({ mois: null, annee: String(now.getFullYear()) })
    } else {
      setFilter({ mois: null, annee: null })
    }
  }

  const activePeriode = filter.mois ? 'mois' : filter.annee ? 'annee' : 'all'

  const compteFieldClass = (field) => `input ${compteErrors[field] ? 'input-invalid' : ''}`

  const CompteFieldError = ({ field }) =>
    compteErrors[field] ? (
      <p role="alert" className="field-error">
        {compteErrors[field]}
      </p>
    ) : null

  // Initialiser les valeurs à 0 si elles sont null/undefined
  // Solde total = toutes les opérations (somme des comptes ou solde global)
  const soldeTotal = stats.solde_actuel_total ?? stats.solde_actuel ?? 0
  // Solde à ce jour = uniquement les opérations jusqu'à la date du jour
  const soldeACeJour = stats.solde_a_ce_jour ?? soldeTotal
  // Pour les jauges on utilise le solde total (comportement inchangé)
  const soldeReel = soldeTotal

  // S'assurer que parametres a au moins une devise
  const devise = parametres.devise || 'EUR'
  // Pour les jauges et le découvert : utiliser stats.parametres qui contient le découvert cumulé (somme des comptes)
  const paramsForGauges = stats?.parametres ? { ...parametres, devise, ...stats.parametres } : { ...parametres, devise }
  // Solde du mois = somme des comptes (si comptes) sinon variation du mois (entrées - dépenses)
  const soldeMois = stats.solde_mois ?? ((stats.entrees_mois ?? 0) - (stats.depenses_mois ?? 0))
  const totalEntrees = stats.total_entrees ?? 0
  const totalDepenses = stats.total_depenses ?? 0
  const entreesMois = stats.entrees_mois ?? 0

  // Vérifier s'il y a des opérations
  const hasOperations = totalEntrees > 0 || totalDepenses > 0
  // Écart entre le solde projeté et le solde réel : c'est la prévision.
  const aVenir = soldeTotal - soldeACeJour

  return (
    <div className="app-page">
      <div className="app-page-header">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="app-page-title">Tableau de bord</h1>
            {(loading || refreshing) && (
              <span className="flex items-center gap-1.5 text-sm text-ink-muted" role="status">
                <Icon name="refresh" size={14} className="animate-spin" />
                {hasLoadedRef.current ? 'Actualisation…' : 'Chargement…'}
              </span>
            )}
          </div>
          <p className="app-page-subtitle">Vue d’ensemble de vos comptes, mouvements et alertes.</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="segmented" role="group" aria-label="Période">
            {PERIODES.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setQuickFilter(p.key)}
                aria-pressed={activePeriode === p.key}
                className={`segmented-item ${activePeriode === p.key ? 'segmented-item-active' : ''}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setShowAddCompteModal(true)} className="btn-secondary">
            <Icon name="plus" size={16} />
            <span className="hidden sm:inline">Ajouter un compte</span>
            <span className="sm:hidden">Compte</span>
          </button>
        </div>
      </div>

      {/* Bloc solde : la donnée que l'on vient chercher en premier, donc
          la seule à ce niveau de taille sur l'écran. */}
      <section className="card overflow-hidden" aria-label="Soldes">
        <div className="grid gap-px bg-line sm:grid-cols-3">
          <div className="bg-surface p-5 sm:p-6">
            <h2 className="overline">Solde à ce jour</h2>
            <Amount
              value={soldeACeJour}
              devise={devise}
              tone={soldeACeJour < 0 ? 'negative' : 'none'}
              className="mt-2 block text-4xl font-semibold sm:text-5xl"
              id="solde-a-ce-jour"
            />
            <p className="mt-2 text-sm text-ink-muted">Opérations passées uniquement</p>
          </div>

          <div className="bg-surface p-5 sm:p-6">
            <h2 className="overline">Solde prévisionnel</h2>
            <Amount
              value={soldeTotal}
              devise={devise}
              tone={soldeTotal < 0 ? 'negative' : 'none'}
              className="mt-2 block text-2xl font-semibold sm:text-3xl"
              id="solde-total"
            />
            <p className="mt-2 flex items-center gap-1.5 text-sm text-ink-muted">
              {aVenir !== 0 && (
                <Icon
                  name={aVenir > 0 ? 'trendUp' : 'trendDown'}
                  size={14}
                  className={aVenir > 0 ? 'text-positive' : 'text-negative'}
                />
              )}
              {aVenir === 0 ? (
                'Aucune opération à venir'
              ) : (
                <>
                  <Amount value={aVenir} devise={devise} signed tone="auto" className="font-medium" />
                  <span>à venir</span>
                </>
              )}
            </p>
          </div>

          <div className="bg-surface p-5 sm:p-6">
            <h2 className="overline">Découvert cumulé</h2>
            <Amount
              value={Number(paramsForGauges.montant_decouvert) || 0}
              devise={devise}
              className="mt-2 block text-2xl font-semibold sm:text-3xl"
              id="solde-decouvert-info"
            />
            <p className="mt-2 text-sm text-ink-muted">Autorisé, tous comptes confondus</p>
          </div>
        </div>

        {comptes.length > 0 && (
          <div className="border-t border-line bg-surface-sunken px-5 py-4">
            <h3 className="overline mb-3">Vos comptes</h3>
            <div className="flex flex-wrap gap-2">
              {comptes.map((c) => {
                const montant = typeof stats?.soldes_comptes_a_ce_jour?.[c.id] === 'number'
                  ? stats.soldes_comptes_a_ce_jour[c.id]
                  : (Number(c.montant) || 0)
                const negatif = montant < 0
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelectedCompte(c)}
                    className="group inline-flex items-center gap-2.5 rounded-md border border-line bg-surface px-3 py-2 text-left transition-colors duration-fast hover:border-line-strong hover:bg-surface-hover"
                  >
                    {/* Une pastille ne signale que l'anomalie : un compte sain
                        n'a pas besoin d'être coloré pour être lu. */}
                    <span
                      aria-hidden="true"
                      className={`dot ${negatif ? 'text-negative' : 'text-ink-disabled'}`}
                    />
                    <span className="text-base font-medium text-ink">{c.nom}</span>
                    <Amount
                      value={montant}
                      devise={devise}
                      tone={negatif ? 'negative' : 'none'}
                      className="text-base text-ink-secondary"
                    />
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </section>

      {!hasOperations && !loading && (
        <div className="callout-accent">
          <Icon name="info" size={18} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Bienvenue sur Finance Prévision.</p>
            <p className="mt-0.5">
              Vous n’avez pas encore d’opérations. Commencez par en ajouter une depuis l’onglet
              Opérations pour voir vos prévisions se construire.
            </p>
          </div>
        </div>
      )}

      {/* Statistiques */}
      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4" aria-label="Statistiques">
        <StatTile label="Total des entrées" value={totalEntrees} devise={devise} tone="positive" icon="arrowDown" />
        <StatTile label="Total des dépenses" value={totalDepenses} devise={devise} tone="negative" icon="arrowUp" />
        <StatTile label="Entrées ce mois" value={entreesMois} devise={devise} tone="positive" icon="calendar" />
        <StatTile label="Solde ce mois" value={soldeMois} devise={devise} tone={soldeMois < 0 ? 'negative' : 'none'} icon="wallet" />
      </section>

      {/* Jauges (paramsForGauges = parametres + découvert cumulé depuis stats) */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Seuils et alertes">
        <Gauge type="etat-compte" solde={soldeReel} params={paramsForGauges} />
        <Gauge type="decouvert" solde={soldeReel} params={paramsForGauges} />
        <Gauge type="seuil" solde={soldeReel} params={paramsForGauges} />
        <Gauge type="minimum" solde={soldeReel} params={paramsForGauges} />
      </section>

      {/* Alertes */}
      {stats.alertes && stats.alertes.length > 0 && (
        <section className="card" aria-label="Alertes actives">
          <div className="card-header">
            <h2 className="card-title flex items-center gap-2">
              <Icon name="alert" size={18} className="text-warning" />
              Alertes actives
            </h2>
            <span className="badge-warning">{stats.alertes.length}</span>
          </div>
          <ul className="divide-y divide-line">
            {stats.alertes.map((alerte) => (
              <li key={alerte.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-5 py-3">
                <div>
                  <span className="text-base font-medium text-ink">{alerte.nom}</span>
                  <span className="ml-2 text-sm text-ink-muted">{alerte.categorie}</span>
                </div>
                <div className="flex items-baseline gap-2 text-sm">
                  <span className="text-ink-muted">
                    <Amount value={alerte.montant_actuel} devise={devise} /> sur{' '}
                    <Amount value={alerte.montant_max} devise={devise} />
                  </span>
                  <span className="badge-negative">
                    +<Amount value={alerte.depassement} devise={devise} />
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Statistiques de période */}
      {stats.filtre_actif && (
        <section className="card" aria-label="Statistiques de la période">
          <div className="card-header">
            <h2 className="card-title">
              Période : <span id="periode-text" className="font-normal text-ink-secondary">{stats.periode}</span>
            </h2>
          </div>
          <div className="grid gap-px bg-line sm:grid-cols-2">
            <div className="bg-surface p-5">
              <h3 className="overline">Entrées</h3>
              <Amount value={totalEntrees} devise={devise} tone="positive" className="mt-1.5 block text-2xl font-semibold" id="periode-entrees" />
            </div>
            <div className="bg-surface p-5">
              <h3 className="overline">Dépenses</h3>
              <Amount value={totalDepenses} devise={devise} tone="negative" className="mt-1.5 block text-2xl font-semibold" id="periode-depenses" />
            </div>
          </div>
        </section>
      )}

      {/* Détail d'un compte */}
      {selectedCompte && (
        <div className="modal-backdrop" onClick={closeCompteModals} role="presentation">
          <div
            className="modal-panel"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="compte-detail-title"
          >
            <div className="modal-header">
              <h3 id="compte-detail-title" className="modal-title">
                {editingCompte ? 'Modifier le compte' : selectedCompte.nom}
              </h3>
              <button type="button" onClick={closeCompteModals} className="btn-icon -mr-1.5" aria-label="Fermer">
                <Icon name="close" size={18} />
              </button>
            </div>

            {editingCompte ? (
              <form onSubmit={handleUpdateCompteSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
                <div className="modal-body space-y-4">
                  <div className="field">
                    <label htmlFor="compte-edit-nom" className="field-label">Nom</label>
                    <input
                      id="compte-edit-nom"
                      type="text"
                      value={editCompteForm.nom}
                      onChange={(e) => setEditCompteForm((f) => ({ ...f, nom: e.target.value }))}
                      aria-invalid={!!compteErrors.nom}
                      className={compteFieldClass('nom')}
                    />
                    <CompteFieldError field="nom" />
                  </div>

                  <div className="field">
                    <label htmlFor="compte-edit-montant" className="field-label">Solde actuel</label>
                    <input
                      id="compte-edit-montant"
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={editCompteForm.montant}
                      onChange={(e) => setEditCompteForm((f) => ({ ...f, montant: e.target.value }))}
                      aria-invalid={!!compteErrors.montant}
                      className={`${compteFieldClass('montant')} text-right tnum`}
                    />
                    <CompteFieldError field="montant" />
                    {/* Effet de bord du serveur : le dire ici évite la surprise d'une
                        opération apparue seule dans l'historique. */}
                    <p className="field-hint">
                      Modifier ce solde crée une opération « Actualisation du compte » de l’écart.
                    </p>
                  </div>

                  <div className="field">
                    <label htmlFor="compte-edit-decouvert" className="field-label">Découvert autorisé</label>
                    <input
                      id="compte-edit-decouvert"
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      value={editCompteForm.montant_decouvert}
                      onChange={(e) => setEditCompteForm((f) => ({ ...f, montant_decouvert: e.target.value }))}
                      aria-invalid={!!compteErrors.montant_decouvert}
                      className={`${compteFieldClass('montant_decouvert')} text-right tnum`}
                    />
                    <CompteFieldError field="montant_decouvert" />
                  </div>

                  <button
                    type="button"
                    onClick={handleDeleteCompte}
                    disabled={savingCompte || deletingCompte}
                    className="btn-danger-ghost btn-sm -ml-2.5"
                  >
                    <Icon name="trash" size={15} />
                    {deletingCompte ? 'Suppression…' : 'Supprimer ce compte'}
                  </button>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={() => { setEditingCompte(false); setCompteErrors({}) }}
                    disabled={savingCompte}
                    className="btn-secondary flex-1"
                  >
                    Annuler
                  </button>
                  <button type="submit" disabled={savingCompte} className="btn-primary flex-1">
                    {savingCompte && <Icon name="refresh" size={15} className="animate-spin" />}
                    {savingCompte ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="modal-body">
                  <div className="data-row">
                    <span className="data-label">Solde à ce jour</span>
                    <Amount
                      value={typeof stats?.soldes_comptes_a_ce_jour?.[selectedCompte.id] === 'number'
                        ? stats.soldes_comptes_a_ce_jour[selectedCompte.id]
                        : (Number(selectedCompte.montant) || 0)}
                      devise={devise}
                      tone={(typeof stats?.soldes_comptes_a_ce_jour?.[selectedCompte.id] === 'number'
                        ? stats.soldes_comptes_a_ce_jour[selectedCompte.id]
                        : (Number(selectedCompte.montant) || 0)) < 0 ? 'negative' : 'none'}
                      className="text-xl font-semibold"
                    />
                  </div>
                  <div className="data-row">
                    <span className="data-label">Découvert autorisé</span>
                    <span className="data-value">
                      <Amount value={Number(selectedCompte.montant_decouvert) || 0} devise={devise} />
                    </span>
                  </div>
                  <div className="data-row">
                    <span className="data-label">Dernière actualisation</span>
                    <span className="data-value font-normal text-ink-secondary">
                      {selectedCompte.updated_at
                        ? new Date(selectedCompte.updated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : selectedCompte.created_at
                          ? new Date(selectedCompte.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                          : '—'}
                    </span>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={handleDeleteCompte}
                    disabled={deletingCompte}
                    className="btn-danger-ghost"
                  >
                    <Icon name="trash" size={15} />
                    {deletingCompte ? 'Suppression…' : 'Supprimer'}
                  </button>
                  <button type="button" onClick={handleEditCompte} className="btn-primary ml-auto">
                    <Icon name="edit" size={15} />
                    Modifier
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Ajouter un compte */}
      {showAddCompteModal && (
        <div
          className="modal-backdrop"
          onClick={() => !addingCompte && closeAddCompteModal()}
          role="presentation"
        >
          <div
            className="modal-panel"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ajout-compte-title"
          >
            <div className="modal-header">
              <h3 id="ajout-compte-title" className="modal-title">Ajouter un compte</h3>
              <button
                type="button"
                onClick={closeAddCompteModal}
                disabled={addingCompte}
                className="btn-icon -mr-1.5"
                aria-label="Fermer"
              >
                <Icon name="close" size={18} />
              </button>
            </div>

            <form onSubmit={handleAddCompteSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
              <div className="modal-body space-y-4">
                <div className="field">
                  <label htmlFor="compte-add-nom" className="field-label">Nom du compte</label>
                  <input
                    id="compte-add-nom"
                    type="text"
                    autoFocus
                    value={addCompteForm.nom}
                    onChange={(e) => setAddCompteForm((f) => ({ ...f, nom: e.target.value }))}
                    placeholder="Compte courant, Livret A…"
                    aria-invalid={!!compteErrors.nom}
                    className={compteFieldClass('nom')}
                  />
                  <CompteFieldError field="nom" />
                </div>

                <div className="field">
                  <label htmlFor="compte-add-montant" className="field-label">Solde actuel</label>
                  <input
                    id="compte-add-montant"
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    placeholder="0,00"
                    value={addCompteForm.montant}
                    onChange={(e) => setAddCompteForm((f) => ({ ...f, montant: e.target.value }))}
                    aria-invalid={!!compteErrors.montant}
                    className={`${compteFieldClass('montant')} text-right tnum`}
                  />
                  <CompteFieldError field="montant" />
                </div>

                <div className="field">
                  <label htmlFor="compte-add-decouvert" className="field-label">
                    Découvert autorisé <span className="font-normal text-ink-muted">(optionnel)</span>
                  </label>
                  <input
                    id="compte-add-decouvert"
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    value={addCompteForm.montant_decouvert}
                    onChange={(e) => setAddCompteForm((f) => ({ ...f, montant_decouvert: e.target.value }))}
                    aria-invalid={!!compteErrors.montant_decouvert}
                    className={`${compteFieldClass('montant_decouvert')} text-right tnum`}
                  />
                  <CompteFieldError field="montant_decouvert" />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={closeAddCompteModal}
                  disabled={addingCompte}
                  className="btn-secondary flex-1"
                >
                  Annuler
                </button>
                <button type="submit" disabled={addingCompte} className="btn-primary flex-1">
                  {addingCompte && <Icon name="refresh" size={15} className="animate-spin" />}
                  {addingCompte ? 'Création…' : 'Créer le compte'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
